import { MapPinIcon } from "@phosphor-icons/react";
import * as Sentry from "@sentry/react";
import { useIntl } from "react-intl";

import { CollapsibleCard } from "@/components/CollapsibleCard/CollapsibleCard";
import type { HotelData } from "@/features/hotels/types/types";

interface HotelLocationCardProps {
  hotel: HotelData;
}

export const HotelLocationCard = ({ hotel }: HotelLocationCardProps) => {
  const intl = useIntl();

  if (!hotel.geolocation && !hotel.address) return null;

  const { latitude, longitude } = hotel.geolocation ?? {};
  const query =
    latitude != null && longitude != null
      ? `${latitude},${longitude}`
      : [hotel.name, hotel.address].filter(Boolean).join(", ");
  const googleMapsUrl = `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=15&output=embed`;

  return (
    <CollapsibleCard
      title={intl.formatMessage({
        defaultMessage: "Location",
        description: "Hotel's location card title",
      })}
      collapsible={false}
    >
      <div className="overflow-hidden rounded-2xl">
        <iframe
          src={googleMapsUrl}
          title={intl.formatMessage({
            defaultMessage: "Hotel location map",
            description: "Title attribute for the embedded map iframe",
          })}
          className="h-48 w-full border-0"
          sandbox="allow-scripts"
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => {
            Sentry.captureException(
              new Error("Google Maps embed iframe failed to load"),
              {
                extra: {
                  googleMapsUrl,
                },
              },
            );
          }}
        />
      </div>

      {hotel.address && (
        <div className="mt-4 flex items-center gap-1">
          <MapPinIcon size={16} className="shrink-0 text-text-primary" />
          <p className="text-sm text-text-primary">{hotel.address}</p>
        </div>
      )}
    </CollapsibleCard>
  );
};
